// =============================================================================
// client/src/pages/campaigns/CampaignBuilder.jsx | v3.0 | SQ-Chrome S1 | P9a — W-8
// =============================================================================
// v3.0 (SQ-Chrome S1, 2026-08-27): CHROME ONLY, applied as a className-only
// substitution rather than a rewrite — 93 lines changed, 93 replaced, and EVERY
// changed line carries a className attribute. No comment, no handler, no string
// literal and no logic moved. Brand tokens map to the board's arbitrary values
// (ink #0E2A20, muted #5A5A5A, paper #F4F4F2); panel borders 1px→2px;
// rounded-lg→rounded-2xl. This file was deliberately NOT hand-rewritten: at
// 1342 lines with 34 canon markers, a substitution is the only edit whose
// safety can be proven from the diff itself.
// =============================================================================
// Five numbered steps on one page. Create at /campaigns/new, edit at
// /campaigns/:id/edit.
//
// 🔴 FORM STATE STAYS IN THE BROWSER UNTIL SAVE (T-3). Nothing is created on
// first touch. The queue is built at CREATE (flux-campaigns.js:390-475) and a
// PATCH of recipient_filters STORES without rebuilding, so after the first Save
// the segment filters lock — a segment mistake after Save means archive the
// draft and start over. Subjects stay editable, because they are read per-row
// at send time.
//
// 🔴 LAUNCH IS THE ONE CLICK IN P9a THAT CROSSES THE IRREVERSIBILITY BOUNDARY.
// It is the last control wired, and it is enabled ONLY when all five hold:
// saved · connect state is `verified` · capacity known and > 0 · no unsaved
// changes · the operator is admin or manager. An RC sees Queue and never Launch.
//
// 🔴 CAPACITY NEVER DEFAULTS OPEN (§6.6, G11). RM initialised capacity to
// {slots_available:20}, swallowed the init error, and enabled Launch on that
// default. Here it starts NULL, the bar reads "Checking capacity…", and a
// failed read or queryFailed reads "Capacity unknown — launch disabled".
//
// The client computes NOTHING that the server decides: composability is stated
// as static help because `analyzeComposability` runs at send; the duration line
// is W-2's `estimate`; the counts are /contacts/count and /segment-count.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { can } from '../../lib/can.js';
import { useMe } from '../../lib/flux/useMe.js';
import { CONNECT_STATES, enablesLaunch, hidesMailboxInput } from '../../lib/flux/connect.js';
// SQ-Chrome S1 (second pass): the board vocabulary, so the Builder's masthead and
// its primary actions stop being the one FluxEngine surface still wearing the old
// chrome. The first pass was a className-only 1:1 substitution — deliberately, on a
// 1342-line file under a clock — which left every element whose original class had
// no direct board equivalent looking untouched. These are those elements.
import { BTN_DARK, BTN_LIGHT, pill } from '../../lib/flux/board.js';
import BodyEditor from '../../components/flux/BodyEditor.jsx';   // S90 P12A item 7
import ConnectGmail from '../../components/flux/ConnectGmail.jsx';
import Toast from '../../components/Toast.jsx';

// §6.3, ported verbatim. All eight pass validateSchedule (startHour < endHour,
// endHour <= 24). `key` is stored so edit mode can reopen the right preset; the
// server validates days/startHour/endHour and ignores the key.
const SEND_WINDOWS = [
  { key: 'mf-9-5', label: 'M–F 9a–5p', days: [1, 2, 3, 4, 5], startHour: 9, endHour: 17 },
  { key: 'mf-9-8', label: 'M–F 9a–8p', days: [1, 2, 3, 4, 5], startHour: 9, endHour: 20 },
  { key: 'mf-3-8', label: 'M–F 3p–8p', days: [1, 2, 3, 4, 5], startHour: 15, endHour: 20 },
  { key: 'mw-9-5', label: 'M–W 9a–5p', days: [1, 2, 3], startHour: 9, endHour: 17 },
  { key: '7d-9-5', label: '7 Days 9a–5p', days: [0, 1, 2, 3, 4, 5, 6], startHour: 9, endHour: 17 },
  { key: '7d-9-8', label: '7 Days 9a–8p', days: [0, 1, 2, 3, 4, 5, 6], startHour: 9, endHour: 20 },
  { key: '7d-3-8', label: '7 Days 3p–8p', days: [0, 1, 2, 3, 4, 5, 6], startHour: 15, endHour: 20 },
  { key: '24-7', label: '24/7', days: [0, 1, 2, 3, 4, 5, 6], startHour: 0, endHour: 24 },
];

const START_MODES = [
  { id: 'immediately', label: 'Immediately' },
  { id: 'midnight', label: 'Midnight' },
  { id: 'tomorrow_9am', label: 'Tomorrow @ 9AM' },
  { id: 'custom', label: 'Pick Date & Time' },
  { id: 'after_auth', label: 'After Gmail Auth', authMode: 'after_auth' },
  { id: 'midnight_after_auth', label: 'Midnight After Auth', authMode: 'midnight_after_auth' },
];

// The eight-token merge vocabulary (crosswalk :148-162). Insert-at-cursor only;
// this file never rewrites a token's casing or spacing — findResidualTokens
// matches what the engine wrote.
const MERGE_TOKENS = ['{{coach_first}}', '{{coach_last}}', '{{coach_name}}', '{{school}}', '{{city}}'];
const SUBJECT_TOKENS = ['{{coach_first}}', '{{coach_last}}', '{{school}}', '{{city}}'];

// §6.5, verbatim.
const SEGMENT_HELP = 'Segments send in order. Later segments auto-exclude contacts already in earlier ones.';
const CATCH_ALL_LABEL = 'EVERYONE NOT IN SEGMENTS ABOVE';
const CATCH_ALL_NAME = 'All Remaining Contacts';
const AI_DISCLOSURE = 'AI Disclosure: These drafts were generated by AI (Claude). Review before sending.';

const MAX_SEGMENTS = 20;
// S90 B3: how long the builder waits after the last interaction before it
// PATCHes. 1500 ms is a typing pause, not a thinking pause — long enough that a
// sentence is one save rather than forty, short enough that the operator never
// reaches for a button that needs a saved campaign and finds it disabled.
const AUTOSAVE_DEBOUNCE_MS = 1500;

// ── S90 P12A item 6 — LINK SLOTS: FRIENDLY LABEL, CANONICAL TOKEN ────────────
// The label is what a football office reads; the TOKEN is what is stored in the
// body and what the send-time merge substitutes. They are deliberately separate
// so the wording can improve without touching a single stored campaign.
//
// 🔴 THE EXTRA SLOTS ARE `extra_link_*`, NOT the punchlist's `Additional_Link_*`,
// AND THAT IS A CORRECTION MADE ON PURPOSE. flux-engine.js's LINK_BLOCK_RX
// already recognises `extra_link_1` and `extra_link_2` as link-bearing tokens.
// Shipping `additional_link_1` instead would mean a body whose ONLY link is an
// extra slot fails the link-block test, is judged non-composable, and silently
// loses variant rotation — the exact failure we spent this session diagnosing on
// 60f600ca. Matching the engine's existing vocabulary costs one word in the UI
// and keeps rotation working. `extra_link_3` is new and is added to that regex.
//
// ⚠ `player_x` LABEL IS AN INFERENCE, NOT DOCUMENTATION. It appears nowhere in
// RecruitMail_Technical_Legacy_Spec_v1.md. Read as X/Twitter because the engine
// groups it with the player-asset links while generic spare slots already exist
// separately as `extra_link_*` — a generic third slot would not have needed its
// own name. Flagged rather than asserted; if the legacy meaning turns up
// different, only this label changes and no stored content moves.
const LINK_SLOTS = [
  { token: 'highlight_link', label: 'Highlight Film' },
  { token: 'player_profile', label: 'Player Profile' },
  { token: 'player_x',       label: 'X / Twitter' },
  { token: 'extra_link_1',   label: 'Extra Link 1' },
  { token: 'extra_link_2',   label: 'Extra Link 2' },
  { token: 'extra_link_3',   label: 'Extra Link 3' },
];

// A link is insertable only once its URL parses AND is http(s). Anything else —
// blank, "www.x.com", "javascript:..." — leaves the insert button disabled, so a
// broken href can never be written into a body that goes to a coach.
function linkUrlState(raw) {
  const v = String(raw || '').trim();
  if (!v) return { ok: false, blank: true, message: null };
  let u;
  try { u = new URL(v); } catch { return { ok: false, blank: false, message: 'Not a valid URL — include https://' }; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return { ok: false, blank: false, message: `Only http(s) links are allowed (got ${u.protocol})` };
  }
  return { ok: true, blank: false, message: null };
}
const DIV_DISPLAY = { FBS: 'DI FBS', FCS: 'DI FCS', DII: 'DII', DIII: 'DIII', NAIA: 'NAIA', JUCO: 'JUCO' };

// Body -> HTML paragraphs. §6.4: a plain textarea storing <p> blocks, no CDN
// script injected into app.ccfrecruiting.com and no new dependency. The pool
// composer needs <p> blocks anyway.
//
// ⚠ RULING 20: trailing whitespace is NOT stripped and NOT normalized. The
// 1200px gap before the unsubscribe footer lives in an existing body, and a
// "tidy" save is how that ruling gets silently reversed.
function toHtmlParagraphs(text) {
  if (/<[a-z][\s\S]*>/i.test(text)) return text;      // already HTML (RM/legacy) — left alone
  return String(text)
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('\n');
}

// ⚠ HISTORY, KEPT BECAUSE THE SHAPE BELOW ONLY MAKES SENSE WITH IT.
// This function was `stripBlankSubject`, and it existed for DEFECT A: the client
// sent `subject: ''` on a field whose own label said "(optional)", the server
// correctly refused supplied-and-empty as distinct from absent, and Save failed
// on the first path every operator takes. The fix was made at the payload
// boundary rather than the initialiser, because emptySegment needed `subject: ''`
// for React to keep the input controlled.
//
// S90 P12A item 2 removed the input, so that constraint is gone with it and
// emptySegment no longer carries the key at all. Defect A can no longer occur
// because the field it was about does not exist.
// S90 P12A item 2 — was stripBlankSubject, which dropped the key only when the
// value was an empty string. Segment subjects are gone entirely now: one subject
// per campaign, every recipient, every segment, every variant. So this drops the
// key unconditionally on the way out, and the server strips it again on the way
// in. Two ends that agree, rather than one trusting the other.
function stripSegmentSubject(seg) {
  if (!seg || typeof seg !== 'object') return seg;
  const { subject: _dropped, ...rest } = seg;
  return rest;                        // the KEY IS GONE, not set to ''
}

// ─── S97 · the flat checkbox list ────────────────────────────────────────────
// 🔴 THE CASCADE IS GONE, AND SO ARE BOTH DROPDOWNS AND EVERY COUNT.
// S96 built a Role dropdown cascading into a Position dropdown, each option
// carrying a match count, because spec §4.4 said a pick matched the term
// ANYWHERE in the title — so a count was the only way to show an operator that
// `Head Coach` also swept up 423 assistants and associates.
//
// §4.4 recorded Jake's ruling BACKWARDS. He was saying that behaviour is
// absurd. With exact segment matching there is nothing for a cascade to
// prevent and nothing for a count to warn about: every checkbox is a string
// that literally exists in the directory, so none of them can return zero.
//
// Jake, S97: "Get rid of the fucking drop downs... I don't need counts. I don't
// need tags or chips or plaques or anything fancy."
function emptySegment() {
  return {
    name: '', catchAll: false,
    // S97: `titleTerms`, a flat array of checked strings, OR-ed together.
    // ⚠ Neither `titleGroups` (S96) nor `title` (free text) is initialised here,
    // and the server still honours BOTH: saved campaigns carry them and their
    // filters are locked at save forever.
    filters: { division: [], region: [], state: [], conference: '', titleTerms: [], priority_groups: [], newFromList: '' },
  };
}

// '2026-09' -> 'September'. The stored value keeps the year; the chip says "New from September List".
function listMonthLabel(ym) {
  const m = /^\d{4}-(0[1-9]|1[0-2])$/.exec(ym || '');
  return m ? new Date(2000, Number(m[1]) - 1, 1).toLocaleString('en-US', { month: 'long' }) : String(ym || '');
}

export default function CampaignBuilder() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();

  const [campaignId, setCampaignId] = useState(routeId || null);
  const [status, setStatus] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [editSeq, setEditSeq] = useState(0);        // S90 B3 — see touch()
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // step 1
  const [name, setName] = useState('');
  const [recruit, setRecruit] = useState(null);
  const [recruitQuery, setRecruitQuery] = useState('');
  const [recruitHits, setRecruitHits] = useState([]);
  const [sendFrom, setSendFrom] = useState('');
  const [connectState, setConnectState] = useState(CONNECT_STATES.IDLE);

  // step 2
  const [segments, setSegments] = useState([]);
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(emptySegment());
  const [draftCount, setDraftCount] = useState(null);
  // 🔴 S101 (cw_273): the estimate calls used to `.catch(() => setCount(null))`,
  // discarding the error. null already means "no filter picked yet", so a failed
  // estimate was INDISTINGUISHABLE from "you have not chosen anything" — and it
  // silently disabled Add Segment, which is the dead-button confusion the S97
  // note below warns about, arriving from a different direction. These hold the
  // reason so the screen can say "we could not check" instead of showing the
  // same blank as "nothing selected".
  const [estimateError, setEstimateError] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [preview, setPreview] = useState(null);       // /segment-count body

  // step 3
  const [startMode, setStartMode] = useState('immediately');
  const [scheduledAt, setScheduledAt] = useState('');
  const [windowKey, setWindowKey] = useState('mf-9-5');
  const [dailyCap, setDailyCap] = useState(450);
  const [sendDelaySeconds, setSendDelaySeconds] = useState(13);
  const [schoolGapSeconds, setSchoolGapSeconds] = useState(180);

  // step 4
  const [links, setLinks] = useState(() => Object.fromEntries(LINK_SLOTS.map((sl) => [sl.token, { url: '', text: '' }])));
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantIdx, setVariantIdx] = useState(0);
  // S90 B2: the pool the SERVER will actually rotate, published by
  // GET /api/flux/campaigns/:id. The client CANNOT compute this — the server
  // collapses a stored 15-variant array to a single body whenever the template
  // is not composable, and only the server runs that test.
  const [variantPool, setVariantPool] = useState(null);
  const [variantsFormat, setVariantsFormat] = useState(null);   // S90 P12A item 9
  const [generating, setGenerating] = useState(false);
  // S96 Y-1: the position selector's vocabulary. Fetched ONCE when the card is
  // opened — not per keystroke, and there is no cache to invalidate because
  // there is nothing to cascade against but this object.
  const [titleVocab, setTitleVocab] = useState(null);
  const [vocabError, setVocabError] = useState('');
  const [allTeamCoaches, setAllTeamCoaches] = useState(true);

  // step 5
  const [capacity, setCapacity] = useState(null);     // 🔴 NULL, never a default
  const [capacityFailed, setCapacityFailed] = useState(false);
  const [launching, setLaunching] = useState(false);

  const subjectRef = useRef(null);
  // S90 P12A item 7/C2 — bodyRef was the textarea's DOM node and is gone with it.
  // The editor exposes insertToken() instead, which splices at the live
  // ProseMirror selection rather than appending. insertAt() survives for the
  // SUBJECT field, which is still a plain input and still wants
  // selectionStart/selectionEnd.
  const bodyEditorRef = useRef(null);
  function insertToken(token) {
    bodyEditorRef.current?.insertToken(token);
    touch();
  }

  const isDraft = !status || status === 'draft';
  const isAdmin = me?.team?.role === 'admin';
  const window8 = SEND_WINDOWS.find((w) => w.key === windowKey) || SEND_WINDOWS[0];

  // S90 B3: every interaction bumps a sequence number as well as the dirty
  // flag. `dirty` alone cannot debounce — once it is true, setDirty(true) is a
  // no-op to React, the effect below never re-runs, and the timer never resets.
  // The counter is what makes each keystroke actually restart the clock.
  const touch = () => { setDirty(true); setEditSeq((n) => n + 1); };

  // ── filter options (priority groups come from the SERVER constant) ────────
  useEffect(() => {
    api.get('/api/flux/campaigns/filter-options')
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  // ── capacity: re-read on entry and before every Queue click (§6.6) ────────
  const loadCapacity = useCallback(() => {
    api.get('/api/flux/campaigns/admin/capacity')
      .then((c) => {
        // The server already answers slots_available:0 + queryFailed:true on a
        // failed count. Either way this NEVER opens the gate.
        setCapacity(c);
        setCapacityFailed(Boolean(c?.queryFailed));
      })
      .catch(() => { setCapacity(null); setCapacityFailed(true); });
  }, []);
  useEffect(() => { loadCapacity(); }, [loadCapacity]);

  // ── recruit typeahead ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!recruitQuery || recruitQuery.length < 2) { setRecruitHits([]); return undefined; }
    const t = setTimeout(() => {
      api.get(`/api/recruits?q=${encodeURIComponent(recruitQuery)}&limit=10`)
        .then((b) => setRecruitHits(b?.recruits || b?.data || []))
        .catch(() => setRecruitHits([]));
    }, 300);
    return () => clearTimeout(t);
  }, [recruitQuery]);

  function pickRecruit(r) {
    setRecruit(r);
    setRecruitQuery('');
    setRecruitHits([]);
    // Sending From defaults to the recruit's own address until /oauth/status
    // reports a connected mailbox, at which point ConnectGmail hands the
    // connected address back through onState and the line below is replaced.
    //
    // 🔴 THIS COMMENT USED TO END "ConnectGmail overwrites it when it does" AND
    // THAT WAS FALSE FOR THE WHOLE OF S84. onState carried the STATE ALONE, so
    // nothing ever wrote the connected address into this field: the card showed
    // the token's mailbox, this field kept the recruit's RECORD email, and Save
    // answered 400 identity mismatch — with no hand edit anywhere. The comment
    // is why nobody looked. The wire it describes now exists (S85).
    setSendFrom(r?.email || '');
    setConnectState(CONNECT_STATES.CHECKING);
    touch();
  }

  // ── edit mode: fill every field from the row ──────────────────────────────
  useEffect(() => {
    if (!routeId) return;
    api.get(`/api/flux/campaigns/${routeId}`)
      .then((c) => {
        setCampaignId(c.id);
        setStatus(c.status);
        setName(c.name || '');
        setSendFrom(c.send_from_email || '');
        setRecruit(c.recruit_id ? { id: c.recruit_id, first_name: c.recruitName || '', email: c.send_from_email } : null);
        setSegments(Array.isArray(c.recipient_filters) ? c.recipient_filters : []);
        setDailyCap(c.daily_cap ?? 450);
        setSendDelaySeconds(c.send_delay_seconds ?? 13);
        setSchoolGapSeconds(c.school_gap_seconds ?? 180);
        // schedule.key first; else match a preset by its three fields; else Custom.
        const sched = c.schedule || {};
        const byKey = SEND_WINDOWS.find((w) => w.key === sched.key);
        const byShape = SEND_WINDOWS.find((w) => w.startHour === sched.startHour && w.endHour === sched.endHour
          && JSON.stringify([...(w.days || [])].sort()) === JSON.stringify([...(sched.days || [])].sort()));
        setWindowKey((byKey || byShape)?.key || 'custom');
        setStartMode(c.start_mode || (c.scheduled_at ? 'custom' : 'immediately'));
        setScheduledAt(c.scheduled_at || '');
        const tv = Array.isArray(c.template_variants) ? c.template_variants[0] : null;
        if (tv) {
          setBody(tv.body || '');
          setLinks({ ...links, ...(tv.links || {}) });
          setVariants(Array.isArray(tv.aiVariants) ? tv.aiVariants : []);
          setVariantsFormat(tv.aiVariantsFormat || null);   // S90 — absent = legacy middles
        }
        setSubject(c.subject || '');
        setVariantPool(c.variantPool || null);   // S90 B2
        setDirty(false);
      })
      .catch((err) => setToast({ tone: 'error', message: err.body?.error || err.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // ── S97 · the checkbox list's helpers ────────────────────────────────────
  function loadTitleVocab() {
    setVocabError('');
    api.get('/api/flux/contacts/title-vocab')
      .then((v) => setTitleVocab(v))
      .catch((err) => setVocabError(err.body?.error || err.message || 'Could not read the coach directory.'));
  }

  // One toggle, no cascade, nothing to strand. ⚠ Order is preserved as clicked
  // rather than re-sorted, so the chip line reads back in the order the operator
  // built it.
  function toggleTitleTerm(value) {
    setDraft((d) => {
      const cur = d.filters.titleTerms || [];
      const next = cur.includes(value) ? cur.filter((t) => t !== value) : [...cur, value];
      return { ...d, filters: { ...d.filters, titleTerms: next } };
    });
  }

  // ── the draft segment's live count ────────────────────────────────────────
  useEffect(() => {
    // ⚠ S97: `titleTerms` MUST be tested here. If this gate never sees the new
    // field, the live draft count never fires for a title-only segment and the
    // Add Segment button stays disabled — and the failure looks like a dead
    // button, not like a filter bug. The same trap caught S96 with titleGroups.
    const hasFilter = draft.filters.division.length || draft.filters.region.length || draft.filters.state.length
      || draft.filters.conference || draft.filters.titleTerms?.length || draft.filters.priority_groups.length
      || draft.filters.newFromList;
    if (!hasFilter) { setDraftCount(null); setEstimateError(null); return undefined; }
    const t = setTimeout(() => {
      setEstimateError(null);
      api.post('/api/flux/campaigns/audience/estimate', { filters: draft.filters })
        .then((b) => { setDraftCount(b?.total ?? null); setEstimateError(null); })
        .catch((err) => {
          setDraftCount(null);
          setEstimateError(err.timeout
            ? "Couldn't check — the count timed out. Try again."
            : "Couldn't check the audience size right now.");
        });
    }, 400);
    return () => clearTimeout(t);
  }, [draft]);

  // ── the preview + W-2 duration estimate ───────────────────────────────────
  useEffect(() => {
    if (segments.length === 0) { setPreview(null); setPreviewError(null); return undefined; }
    const t = setTimeout(() => {
      api.post('/api/flux/campaigns/audience/estimate', {
        segments,
        schedule: { days: window8.days, startHour: window8.startHour, endHour: window8.endHour },
        dailyCap: Number(dailyCap) || 450,
        sendDelaySeconds: Number(sendDelaySeconds),
        // S90 P12A item 5 — the same-school gap is frequently what binds the
        // runtime, so the estimate cannot be honest without it.
        schoolGapSeconds: Number(schoolGapSeconds) || 180,
      })
        .then((b) => { setPreview(b); setPreviewError(null); })
        .catch((err) => {
          setPreview(null);
          setPreviewError(err.timeout
            ? "Couldn't estimate this campaign — the request timed out. Try again."
            : "Couldn't estimate this campaign right now.");
        });
    }, 600);
    return () => clearTimeout(t);
  }, [segments, windowKey, dailyCap, sendDelaySeconds, schoolGapSeconds, window8]);

  // ── the D-14 priority-group coupling rule ─────────────────────────────────
  // 🔴 P4 W-16's validator 400s when priority_groups:['pg1'] is present without
  // ALL TWELVE of its state codes. So the chip is the ONLY writer of the group
  // id, and de-selecting any one of its states drops the id and leaves the rest
  // as plain state filters. The payload can then never trip the validator.
  function togglePriorityGroup(group) {
    setDraft((d) => {
      const on = d.filters.priority_groups.includes(group.id);
      if (on) {
        // OFF: drop the id and its twelve codes, but LEAVE any state the
        // operator added independently.
        return {
          ...d,
          filters: {
            ...d.filters,
            priority_groups: d.filters.priority_groups.filter((g) => g !== group.id),
            state: d.filters.state.filter((s) => !group.states.includes(s)),
          },
        };
      }
      return {
        ...d,
        filters: {
          ...d.filters,
          priority_groups: [...d.filters.priority_groups, group.id],
          state: [...new Set([...d.filters.state, ...group.states])],
        },
      };
    });
  }

  function toggleState(code) {
    setDraft((d) => {
      const on = d.filters.state.includes(code);
      const state = on ? d.filters.state.filter((s) => s !== code) : [...d.filters.state, code];
      // Removing a state that belongs to an active group drops the GROUP ID.
      // Re-selecting all twelve by hand does NOT re-add it — the chip is the
      // only writer, or the chip's visual state becomes a guess.
      const groups = (options?.priority_groups || []);
      const priority_groups = d.filters.priority_groups.filter((gid) => {
        const g = groups.find((x) => x.id === gid);
        return !g || g.states.every((s) => state.includes(s));
      });
      return { ...d, filters: { ...d.filters, state, priority_groups } };
    });
  }

  function toggleIn(key, value) {
    setDraft((d) => {
      const cur = d.filters[key];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...d, filters: { ...d.filters, [key]: next } };
    });
  }

  function addSegment() {
    if (segments.length >= MAX_SEGMENTS) return;
    // Inserted BEFORE the catch-all, which is always last (RM :384).
    setSegments((list) => {
      const catchIdx = list.findIndex((s) => s.catchAll);
      const seg = { ...draft, name: draft.name || `Segment ${list.length + 1}` };
      if (catchIdx === -1) return [...list, seg];
      return [...list.slice(0, catchIdx), seg, ...list.slice(catchIdx)];
    });
    setDraft(emptySegment());
    setDraftCount(null);
    touch();
  }

  function addCatchAll() {
    if (segments.some((s) => s.catchAll)) return;
    setSegments((list) => [...list, { name: CATCH_ALL_NAME, catchAll: true, filters: {} }]);
    touch();
  }

  function moveSegment(i, dir) {
    setSegments((list) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return list;
      if (list[j].catchAll || list[i].catchAll) return list;   // nothing moves past the catch-all
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    touch();
  }

  function removeSegment(i) {
    setSegments((list) => list.filter((_, k) => k !== i));
    touch();
  }

  function insertAt(ref, setter, token) {
    const el = ref.current;
    if (!el) { setter((v) => v + token); touch(); return; }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    setter((v) => v.slice(0, start) + token + v.slice(end));
    touch();
  }

  // ── variants ──────────────────────────────────────────────────────────────
  async function generateVariants() {
    setGenerating(true);
    try {
      const r = await api.post('/api/flux/variants/generate', { body: toHtmlParagraphs(body), links });
      setVariants(r?.variants || []);
      // S90 P12A item 9 — take the format from the SERVER's response rather than
      // hardcoding 'full' here. If the generator's shape ever changes again, the
      // stamp changes with it and nothing on this side has to be remembered.
      setVariantsFormat(r?.format || null);
      setVariantIdx(0);
      touch();
    } catch (err) {
      setToast({ tone: 'error', message: err.body?.error || err.message });
    } finally { setGenerating(false); }
  }

  async function regenerateOne(i) {
    // ── 🔴 S94 X-1 — THIS IS THE MINT PATH, AND BOTH OBVIOUS FIXES ARE WRONG ────
    // /regenerate returns a WHOLE email under the current prompt. Dropping one
    // into an array whose stored format is legacy is exactly the defect that
    // reached Jake's inbox (message 1fe9ffce…, 2026-08-27T23:15:46).
    //
    // ⚠ AND STAMPING THE ARRAY 'full' FROM ONE SLOT IS THE MIRROR DEFECT, NOT THE
    // FIX. aiVariantsFormat is a property of the ARRAY. Setting it from a single
    // regenerated entry tells the server to render the OTHER 14 legacy middles as
    // whole emails — which drops the greeting and mails a coach a message that
    // starts mid-sentence, the failure flux-variant-pool.js names 4818b741 by id
    // to prevent. There is no per-entry format to make this safe.
    //
    // So the operation is REFUSED rather than made to guess, and the operator is
    // pointed at the one action that IS coherent: regenerate all fifteen, which
    // stamps the whole array in one write. Same remedy the rotation-disabled
    // banner below already recommends.
    if (variantsFormat !== 'full') {
      setToast({
        tone: 'error',
        message: 'These versions came from an older generator. Press “Generate 15 versions” to remake them all — regenerating a single one would mix two formats in the same campaign.',
      });
      return;
    }
    try {
      const r = await api.post('/api/flux/variants/regenerate', { body: toHtmlParagraphs(body), links, index: i });
      // A failed slot is NULL, never RM's placeholder text (T-11).
      setVariants((v) => v.map((x, k) => (k === i ? (r?.variant ?? null) : x)));
      // Take the format off the response the way generateVariants does, rather
      // than assuming it. Safe here and ONLY here: the guard above has already
      // established the array is 'full', so this cannot change the array's shape
      // — it keeps the two in step if the generator's format ever moves again.
      setVariantsFormat(r?.format || variantsFormat);
      touch();
    } catch (err) {
      setToast({ tone: 'error', message: err.body?.error || err.message });
    }
  }

  // ── save ──────────────────────────────────────────────────────────────────
  function payload() {
    return {
      name,
      recruitId: recruit?.id,
      sendFrom,
      subject,
      segments: segments.map(stripSegmentSubject),
      // S90 P12A item 9 — aiVariantsFormat rides with the variants it describes.
      // Absent means legacy middle-only, which is what every pre-S90 campaign
      // holds; the server reads it rather than sniffing the content.
      templateVariants: [{ body: toHtmlParagraphs(body), links, aiVariants: variants, aiVariantsFormat: variantsFormat }],
      sendType: startMode === 'immediately' ? 'immediate' : 'scheduled',
      scheduledAt: startMode === 'custom' && scheduledAt ? new Date(scheduledAt).toISOString() : null,
      startMode: START_MODES.find((m) => m.id === startMode)?.authMode || null,
      dailyCap: Number(dailyCap) || 450,
      sendDelaySeconds: Number(sendDelaySeconds),
      schoolGapSeconds: Number(schoolGapSeconds),
      schedule: { key: window8.key, days: window8.days, startHour: window8.startHour, endHour: window8.endHour },
    };
  }

  async function save() {
    setSaving(true);
    try {
      if (!campaignId) {
        // 🔴 DEFECT B. POST /api/flux/campaigns answers { success, campaignId }
        // (flux-campaigns.js:617). This read `created?.campaign?.id ||
        // created?.id` — NEITHER KEY EXISTS — so campaignId stayed undefined,
        // the saved condition never became true, Launch could never enable, and
        // a SECOND Save press would have POSTed a SECOND CREATE because save()
        // branches on !campaignId. A duplicate campaign with a duplicate queue
        // behind it.
        const created = await api.post('/api/flux/campaigns', payload());
        const newId = created?.campaignId;
        if (!newId) {
          // Never silently continue as if unsaved. If the shape changes again
          // the operator finds out here, not at a Launch button that will not
          // enable for a reason nothing states.
          throw new Error('Campaign was created but the server did not return a campaignId — reload before editing.');
        }
        setCampaignId(newId);
        setStatus('draft');
        setDirty(false);
        // The URL, the badge and the database stop disagreeing.
        navigate(`/campaigns/${newId}/edit`, { replace: true });
        setToast({ tone: 'success', message: 'Saved.' });
        return;
      } else {
        // PATCH takes only the allowed keys. status / pause_reason / sendFrom /
        // recruitId are NEVER patchable, and recipientFilters must stay the same
        // length — subjects only, after the first save (T-3, T-12).
        const p = payload();
        await api.patch(`/api/flux/campaigns/${campaignId}`, {
          name: p.name,
          subject: p.subject,
          recipientFilters: segments.map(stripSegmentSubject),
          templateVariants: isDraft ? p.templateVariants : undefined,
          sendType: p.sendType,
          scheduledAt: p.scheduledAt,
          dailyCap: p.dailyCap,
          sendDelaySeconds: p.sendDelaySeconds,
          schoolGapSeconds: p.schoolGapSeconds,
          schedule: p.schedule,
          startMode: p.startMode,
        });
      }
      setDirty(false);
      setToast({ tone: 'success', message: 'Saved.' });
    } catch (err) {
      setToast({ tone: 'error', message: err.body?.error || err.message });
    } finally { setSaving(false); }
  }

  // ── S90 B3: AUTOSAVE ON INTERACTION ───────────────────────────────────────
  // Jake: "There's NO REASON TO MAKE ME SAVE A CAMPAIGN BEFORE I SEND THE TEST
  // EMAIL. Just save it every time there's interaction."
  //
  // 🔴 PATCH ONLY, AND THE CARVE-OUT IS NOT LAZINESS — IT IS THE WHOLE SAFETY
  // ARGUMENT. save() branches on !campaignId: with no id it POSTs
  // /api/flux/campaigns, and that route does not merely create a row — when
  // segments are present it runs the S88 queue build and PUBLISHES
  // email_messages. Firing that on a debounce timer would manufacture a queue
  // the operator never asked for, from a half-typed campaign, without a press.
  // So the FIRST save on a brand-new campaign stays a deliberate act. Every
  // save after it is a PATCH, and PATCH cannot touch queue_build_state at all
  // (verified: no write to that column anywhere in server/src outside tests),
  // which is exactly the line cw_058 §2.5 asked us to hold.
  //
  // Draft-only for the same class of reason: PATCH answers 409 "Dispatch-
  // relevant campaign fields cannot change while sending" on a live campaign,
  // and a timer that re-fires that would nag the operator on a loop.
  //
  // The seq guard is what stops a FAILED autosave retrying forever: save()
  // leaves `dirty` true when it throws, and `saving` flipping back to false
  // would otherwise re-trigger this effect immediately. Stamping the attempt
  // means a failure waits for the next real interaction, not for a timer.
  const saveRef = useRef(null);
  saveRef.current = save;
  const lastAutosavedSeq = useRef(0);
  useEffect(() => {
    if (!campaignId || !dirty || saving || !isDraft) return undefined;
    if (lastAutosavedSeq.current >= editSeq) return undefined;
    const t = setTimeout(() => {
      lastAutosavedSeq.current = editSeq;
      saveRef.current?.();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [campaignId, dirty, saving, isDraft, editSeq]);

  // ── send test — NO RECIPIENT INPUT (ruling 35) ────────────────────────────
  async function sendTest() {
    try {
      const r = await api.post('/api/flux/send/test', {
        campaignId,
        variantIndex: poolIdx,   // S90 B2: a SERVER pool index, clamped at render
      });
      // S90 B2: the server clamps rather than 400s, so if it used a different
      // body than the one on screen the operator is told, not left to assume.
      const swapped = Number.isInteger(r?.variant_used) && Number.isInteger(r?.variant_requested)
        && r.variant_used !== r.variant_requested;
      setToast({
        tone: swapped ? 'error' : 'success',
        message: swapped
          ? `Test sent to ${me?.team?.email} using the ORIGINAL body — variant ${r.variant_requested} does not exist for this campaign (pool is ${r.variant_pool_size}).`
          : `Test sent to ${me?.team?.email} — subject: ${r?.rendered_subject || ''}`,
      });
    } catch (err) {
      const tokens = err.body?.tokens;
      setToast({
        tone: 'error',
        message: tokens?.length
          ? `Unresolved merge fields: ${tokens.join(' ')} — fix the template`
          : (err.body?.error || err.message),
      });
    }
  }

  // ── queue / launch ────────────────────────────────────────────────────────
  async function queueOnly() {
    loadCapacity();
    try {
      const q = await api.post(`/api/flux/campaigns/${campaignId}/queue`, {});
      // 🔴 FOUND BY THE SWEEP, not by the walk. POST /:id/queue answers
      // { success, total_queued } (flux-campaigns.js:1544) — NOT `queued`.
      // This read q?.queued ?? 0, so the toast said "Queued 0 emails" no matter
      // how many rows were built. Wrong in the REASSURING direction, which is
      // the worst way for a number to be wrong.
      setToast({ tone: 'success', message: `Queued ${q?.total_queued ?? 0} emails${q?.gmail_note ? ` — ${q.gmail_note}` : ''}` });
      return q;
    } catch (err) {
      // 503 F-24 · 409 scheduled/start_mode/cooldown/CAS · 429 cap — all verbatim.
      setToast({ tone: 'error', message: err.body?.error || err.message });
      return null;
    }
  }

  async function queueAndLaunch() {
    setLaunching(true);
    const q = await queueOnly();
    if (!q) { setLaunching(false); return; }
    try {
      await api.post(`/api/flux/send/launch/${campaignId}`, {});
      // 🔴 J-4. THE 202 IS NOT AN OUTCOME — IT IS A TIMEOUT.
      // flux-send.js races the launch against LAUNCH_RACE_MS (1500) and answers
      // 202 {accepted:true, note:'Run started…'} the moment that timer wins,
      // whether or not the run then succeeds. This client used to render that
      // as "Send loop running" unconditionally, so a genuine refusal past the
      // 1500 ms mark reached the operator as a GREEN TOAST OVER A NO-OP.
      // The ledger row says it outright: read the ROW, never the 202.
      let status = null;
      let reread = true;
      try {
        const row = await api.get(`/api/flux/campaigns/${campaignId}`);
        status = row?.status ?? null;
      } catch {
        reread = false;             // the launch may be fine; we simply do not know
      }
      if (!reread) {
        setToast({ tone: 'error', message: 'Launch was accepted but its status could not be confirmed — open the campaign page and check before assuming it is running.' });
      } else if (status === 'sending' || status === 'queued') {
        setToast({ tone: 'success', message: `Send loop running (${status}) — track it on the campaign page.` });
      } else {
        // ⚠ Names the status verbatim rather than paraphrasing. "paused" and
        // "draft" fail for completely different reasons and the operator needs
        // to know which one they are looking at.
        setToast({ tone: 'error', message: `Launch did not start — the campaign is still "${status ?? 'unknown'}". Open the campaign page for the reason.` });
      }
      // Navigate either way: the campaign page renders the row, which is the
      // authority this toast is only reporting.
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      setToast({ tone: 'error', message: err.body?.error || err.message });
    } finally { setLaunching(false); }
  }

  // 🔴 PART 1 OF JAKE'S CONNECTED-STATE RULING: hide the Sending-from input once
  // connected. `hidesMailboxInput` is a SEPARATE predicate from the one gating
  // the Connect buttons, and the separation is the fix, not a preference.
  //
  // ⚠ THIS LINE READ `!recruit || offersConnectControls(connectState)` AND THAT
  // SHIPPED A REGRESSION TO PRODUCTION. `offersConnectControls` is the BUTTONS'
  // list. `checking` is not on it — correctly, there is nothing to press during
  // a status round trip — so keying the FIELD on it unmounted the input every
  // time the 600 ms hand-edit debounce fired `checkStatus()`. MEASURED IN A
  // BROWSER at S85 on `not_connected`, the primary path: type, pause two
  // seconds, and every keystroke after the pause LANDS NOWHERE. The field keeps
  // its old value and loses focus, so there is no error and no visible tell.
  //
  // ⚠ BOTH OF US READ THE CODE AND BOTH GOT THE SYMPTOM WRONG — the prediction
  // was "the field vanishes mid-typing"; it does not stay gone and the value is
  // not lost. It is silent keystroke loss, which is worse and looks like
  // nothing. A four-minute browser walk settled what two code-reads could not.
  //
  // `hidesMailboxInput` hides on `connected` and `verified` ONLY — a token is on
  // file and there is nothing to retype. Transients (`checking`, `idle`) and
  // every reachable-input state show the field, so no debounce can take it away.
  // See connect.js for why `revoked` is deliberately excluded (OC's S85 call).
  //
  // ⚠ AND THE `!recruit` GUARD IS GONE, not dropped by accident: `idle` is not a
  // hiding state, so a blank form shows the field without needing a special
  // case. The departure that guard existed to cover no longer exists.
  const showSendFromInput = !hidesMailboxInput(connectState);

  // ── S90 B2: ONE MEANING FOR `variantIndex`, ON BOTH SIDES OF THE WIRE ──────
  // 🔴 There were TWO disagreements here, not one.
  //
  //  (1) SIZE. The carousel counted `variants.length` — the stored aiVariants —
  //      while the server counts resolveVariantPool().bodies.length, which
  //      collapses to 1 whenever the template is not composable. Campaign
  //      60f600ca had 15 stored variants, a body with no <a> tag and no
  //      configured links, and logged `variant rotation disabled … no
  //      link/closing block found` on every one of its seven real sends. The UI
  //      offered "Variant 15 of 15" for a rotation that could never fire, the
  //      operator picked one, and the server 400'd index 14 against a pool of 1.
  //
  //  (2) MEANING, and this one was silent. The server's pool is
  //      [original, ...aiVariants], so ITS index 0 is the ORIGINAL body. The
  //      carousel's index 0 was aiVariants[0]. Every index was off by one:
  //      picking "Variant 1" test-sent the original, "Variant 2" sent
  //      aiVariants[0]. Nothing surfaced that, because nothing compared them.
  //
  // Both close the same way — the carousel now indexes the SERVER's pool
  // directly. Index 0 is labelled Original; 1..N-1 are the variants.
  const effectivePool = variantPool?.effective ?? (variants.length ? variants.length + 1 : 1);
  const rotationDisabledReason = variantPool?.rotationDisabledReason || null;
  // Clamped at render, so a stale index from a pool that has since shrunk can
  // never be posted. The server clamps too — this is the belt to that braces,
  // and it is what keeps the on-screen label honest in the same tick.
  const poolIdx = Math.min(Math.max(0, variantIdx), Math.max(0, effectivePool - 1));

  // 🔴 THE FIVE CONDITIONS. All of them, every time.
  const capacityKnown = capacity && !capacityFailed && Number.isInteger(capacity.slots_available);
  // S90 P12A items 10+17 — `slots_available > 0` is GONE from this list.
  // Capacity being FULL no longer refuses anything: the server admits the queue
  // and the campaign parks in 'queued' until a send slot frees (Jake: "anything
  // over 20 should just queue and wait to send until one finishes"). A client
  // that kept disabling Launch here would block exactly what the server now
  // accepts.
  // 🔴 `capacityKnown` STAYS, and that is deliberate. G11/section 6.6 says
  // capacity never defaults open, and the server still answers 503 when the
  // capacity query fails — so an UNREADABLE capacity must still close the gate.
  // Full and unknown are different states and only one of them is now benign.
  const launchEnabled = Boolean(campaignId)
    && enablesLaunch(connectState)
    && capacityKnown
    && !dirty
    && isAdmin
    && !launching;
  const startsNow = startMode === 'immediately';

  // Three states, and NONE of them opens the gate by default (§6.6, G11):
  //   not yet read   -> 'Checking capacity…'
  //   read and bad   -> 'Capacity unknown — launch disabled'   (HTTP fail OR queryFailed)
  //   read and good  -> the real numbers
  // S90 P12A item 10 — the running/slots telemetry is GONE. It exposed queue
  // internals to a football office and, now that a full board simply queues, the
  // number it reported no longer changes anything the operator can act on.
  // What survives is only the two states that still gate the button: capacity
  // not yet read, and capacity unreadable. A healthy board says nothing at all.
  const capacityLine = capacityFailed
    ? 'Capacity unknown — launch disabled'
    : (capacity === null ? 'Checking capacity…' : null);

  const totalNet = preview?.dedupedTotal ?? null;
  const estimate = preview?.estimate ?? null;

  const priorityGroups = options?.priority_groups || [];

  return (
    <div className="min-h-screen bg-[#F4F4F2]">
      <header className="bg-white border-b-2 border-[#0E2A20]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <button type="button" onClick={() => navigate('/campaigns')} className="text-sm text-[#5A5A5A] hover:text-[#0E2A20]">← Back</button>
            <h1 className="font-display font-black text-[#0E2A20] text-2xl mt-1">{campaignId ? 'Edit Campaign' : 'New Campaign'}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* 🔴 SAVE LIVES DOWN IN THE SEND ROW NOW, NOT HERE.
                Autosave (S90 B3) covers every save EXCEPT the first one on a
                brand-new campaign — that one stays a deliberate press because
                POST /api/flux/campaigns builds the queue. So on exactly the
                screen where Save is mandatory, it was a full page-scroll away
                from the buttons it gates. Jake, SQ-Chrome S1: "the SAVE button
                needs to be right next to the Send Test Email button and not in
                the masthead where you have to scroll to find it."
                MOVED, NOT COPIED — one Save button, next to what it unblocks. */}
            <span className={`${pill(false)} cursor-default`}>
              {campaignId ? (status || 'draft') : 'NEW'}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* ── 1 BASICS ──────────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border-2 border-[#0E2A20] p-6 space-y-4">
          <h2 className="font-display font-black text-[#0E2A20] text-xl">1. Basics</h2>
          <label className="block">
            <span className="text-sm text-[#5A5A5A]">Campaign name</span>
            <input value={name} onChange={(e) => { setName(e.target.value); touch(); }}
              className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
          </label>
          <div className="text-sm text-[#5A5A5A]">
            Operator: <span className="text-[#0E2A20]">{me?.team?.full_name || '—'}</span>
          </div>

          <div>
            <span className="text-sm text-[#5A5A5A]">Player</span>
            {recruit ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-[#0E2A20]">{recruit.first_name} {recruit.last_name || ''}</span>
                {isDraft ? (
                  <button type="button" onClick={() => { setRecruit(null); setSendFrom(''); setConnectState(CONNECT_STATES.IDLE); touch(); }}
                    className="text-xs text-[#5A5A5A] hover:text-[#0E2A20]">change</button>
                ) : null}
              </div>
            ) : (
              <>
                <input value={recruitQuery} onChange={(e) => setRecruitQuery(e.target.value)} placeholder="Search recruits…"
                  className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
                {recruitHits.length > 0 ? (
                  <ul className="border-2 border-[#0E2A20] rounded mt-1 divide-y divide-gray-100">
                    {recruitHits.map((r) => (
                      <li key={r.id}>
                        <button type="button" onClick={() => pickRecruit(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F4F4F2]">
                          {r.first_name} {r.last_name} {r.email ? <span className="text-[#5A5A5A]">· {r.email}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>

          <label className="block">
            <span className="text-sm text-[#5A5A5A]">Sending from</span>
            {showSendFromInput ? (
              <>
                <input
                  value={sendFrom}
                  disabled={!isDraft}
                  // 🔴 `setConnectState(CHECKING)` USED TO SIT HERE AND HAD TO GO.
                  // Now that this input's visibility keys on the connect state,
                  // asserting CHECKING on a keystroke would hide the field the
                  // operator is mid-way through typing into — CHECKING is not in
                  // STATES_OFFERING_CONNECT. ConnectGmail owns the connect state
                  // and re-checks on its own 600 ms debounce; the builder does
                  // not get to assert it. The launch gate loses nothing: touch()
                  // sets `dirty`, and `!dirty` is one of the five conditions.
                  onChange={(e) => { setSendFrom(e.target.value); touch(); }}
                  className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm disabled:bg-[#F4F4F2]"
                />
                {!isDraft ? <span className="text-xs text-[#5A5A5A]">The sending mailbox is fixed once a campaign leaves draft.</span> : null}
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-[#0E2A20]">{sendFrom || '—'}</p>
              </>
            )}
          </label>

          {/* 🔴 onState now hands back the CONNECTED ADDRESS with the state.
              Without this second argument the builder could learn that a token
              exists and never learn which mailbox it is — which is how
              `sendFrom` kept the recruit's record email while the card showed
              the token's, and Save answered 400. Guarded on inequality so a
              state change reporting the same address is not a re-render, and
              NOT touch()ed: the server reporting its own truth is not an
              operator edit, and marking the form dirty would block Launch on a
              change nobody made. */}
          {recruit ? (
            <ConnectGmail
              recruitId={recruit.id}
              gmail={sendFrom}
              onState={(next, address) => {
                setConnectState(next);
                if (address) setSendFrom((cur) => (cur === address ? cur : address));
              }}
            />
          ) : null}
        </section>

        {/* ── 2 SEGMENTS ────────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border-2 border-[#0E2A20] p-6 space-y-4">
          <h2 className="font-display font-black text-[#0E2A20] text-xl">2. Segments</h2>
          <p className="text-sm text-[#5A5A5A]">{SEGMENT_HELP}</p>

          {!isDraft ? (
            <p className="text-sm text-amber-800">
              Segment filters are locked — the send queue was built when this campaign was created, and
              changing a filter now would not rebuild it. Subjects stay editable.
            </p>
          ) : null}

          <div className="space-y-2">
            {segments.map((s, i) => (
              <div key={`${s.name}-${i}`} className="border-2 border-[#0E2A20] rounded p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#0E2A20]">
                    {i + 1}. {s.catchAll ? CATCH_ALL_LABEL : s.name}
                  </span>
                  {isDraft ? (
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveSegment(i, -1)} disabled={i === 0 || s.catchAll} className="text-xs px-2 py-0.5 border-2 border-[#0E2A20] rounded disabled:opacity-30">↑</button>
                      <button type="button" onClick={() => moveSegment(i, 1)} disabled={s.catchAll || segments[i + 1]?.catchAll || i === segments.length - 1} className="text-xs px-2 py-0.5 border-2 border-[#0E2A20] rounded disabled:opacity-30">↓</button>
                      <button type="button" onClick={() => removeSegment(i)} className="text-xs px-2 py-0.5 border-2 border-[#0E2A20] rounded">✕</button>
                    </div>
                  ) : null}
                </div>
                {!s.catchAll ? (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {/* 🔴 S96 Y-1 — THIS RENDERER CRASHED ON titleGroups AND THE CRASH WAS THE
                        REASON TO TOUCH IT, NOT THE LABEL. Every filter value used to be a string
                        or an array of strings, so `{x}` was always a valid React child.
                        titleGroups is an array of OBJECTS, and React throws "Objects are not
                        valid as a React child" — taking down the whole segment list for any
                        saved campaign carrying one. Not blank, not [object Object]: a crash.
                        ⚠ THE LABEL FORMAT BELOW IS CC'S CALL FILLING A GAP OC EXPLICITLY LEFT
                        OPEN, chosen only because the alternative was shipping the crash. It is
                        one line to change and OC/Jake own the wording. */}
                    {Object.entries(s.filters || {}).flatMap(([k, v]) => {
                      if (k === 'titleGroups') {
                        return (Array.isArray(v) ? v : []).map((g, gi) => {
                          const label = [g?.role, g?.position].filter(Boolean).join(' · ');
                          return label
                            ? <span key={`${k}-${gi}`} className="text-xs px-2 py-0.5 rounded bg-[#F4F4F2] text-[#0E2A20]">title: {label}</span>
                            : null;
                        }).filter(Boolean);
                      }
                      if (k === 'newFromList') {
                        // An old saved value keeps resolving to ITS OWN month; it is never rewritten to the newest.
                        return v ? [<span key={k} className="text-xs px-2 py-0.5 rounded bg-[#F4F4F2] text-[#0E2A20]">New from {listMonthLabel(v)} List ({v})</span>] : [];
                      }
                      return (Array.isArray(v) ? v : (v ? [v] : []))
                        .map((x) => <span key={`${k}-${x}`} className="text-xs px-2 py-0.5 rounded bg-[#F4F4F2] text-[#0E2A20]">{k}: {x}</span>);
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {isDraft ? (
            <div className="border-t border-[#0E2A20]/15 pt-4 space-y-3">
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Segment name"
                className="w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={draft.filters.conference} onChange={(e) => setDraft((d) => ({ ...d, filters: { ...d.filters, conference: e.target.value } }))} placeholder="Conference"
                  className="border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
              </div>

              {/* ── S97 · THE COACH CHECKLIST — replaces the S96 Role/Position dropdowns ──
                  The defect this closes is unchanged and is NOT that matching was hard:
                  a wrong guess was SILENT. Typing `QB` returned zero rows, and zero reads
                  identically to "there are no quarterbacks coaches at these schools."
                  🔴 WHAT CHANGED IS THE FIX. S96 answered it with a cascade and a count on
                  every option, because §4.4 made a pick match the term anywhere in the
                  title — so `Head Coach` swept up 423 assistants and associates and the
                  count was the only warning. §4.4 had Jake's ruling BACKWARDS. Matching is
                  now EXACT on the segment, every checkbox is a string that exists in the
                  directory, and no checkbox can return zero. Nothing left to warn about. */}
              <div className="border-2 border-[#0E2A20] rounded p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-[#0E2A20]">
                  <input
                    type="checkbox"
                    checked={allTeamCoaches}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setAllTeamCoaches(on);
                      // 🔴 Checked = NO title constraint at all, which is the unchanged
                      // default path. Clearing the array is what makes that true: an
                      // empty titleTerms never reaches the server's branch, so the
                      // segment is every coach at the selected schools.
                      if (on) setDraft((d) => ({ ...d, filters: { ...d.filters, titleTerms: [] } }));
                      else if (!titleVocab) loadTitleVocab();
                    }}
                  />
                  <span className="font-semibold">Coaches</span>
                </label>

                {allTeamCoaches ? (
                  <p className="text-xs text-[#5A5A5A]">Every coach at the selected schools. Uncheck to pick specific roles and positions.</p>
                ) : (
                  <div className="space-y-2">
                    {vocabError ? <p className="text-xs text-amber-800">{vocabError}</p> : null}
                    {!titleVocab && !vocabError ? <p className="text-xs text-[#5A5A5A]">Reading the directory…</p> : null}

                    {titleVocab ? (
                      <>
                        <p className="text-xs text-[#5A5A5A]">
                          Check any number. A contact matches if it holds any one of them.
                        </p>
                        {/* ⚠ ONE FLAT LIST, roles and player positions together, exactly as
                            asked. No grouping by kind: `kind` comes back on every term and
                            is deliberately not rendered — splitting the list into two
                            headed sections is the dropdown cascade wearing a different hat,
                            and it would put `Defensive Coordinator` in one section while
                            most of its 734 rows sit in the other. */}
                        <div className="max-h-64 overflow-y-auto border-2 border-[#0E2A20]/25 rounded p-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                          {(titleVocab.terms || []).map((t) => (
                            <label key={t.value} className="flex items-center gap-2 text-sm text-[#0E2A20]">
                              <input
                                type="checkbox"
                                checked={(draft.filters.titleTerms || []).includes(t.value)}
                                onChange={() => toggleTitleTerm(t.value)}
                              />
                              <span>{t.value}</span>
                            </label>
                          ))}
                        </div>
                        {/* ⚠ Nothing checked is the SAME state as `Coaches` checked, by a
                            different door: no title constraint, never "match nothing". The
                            server agrees at its own end rather than trusting this one. */}
                        {(draft.filters.titleTerms || []).length === 0
                          ? <p className="text-xs text-amber-800">Nothing checked — this segment is every coach at the selected schools.</p>
                          : null}
                      </>
                    ) : null}
                  </div>
                )}
              </div>

              {options?.new_from_list ? (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">New coaches</div>
                  <div className="flex gap-1 flex-wrap">
                    <button type="button" disabled={segments.some((s) => s.catchAll)}
                      onClick={() => setDraft((d) => ({ ...d, filters: { ...d.filters, newFromList: d.filters.newFromList ? '' : options.new_from_list } }))}
                      className={`text-xs px-2 py-1 rounded border disabled:opacity-40 ${draft.filters.newFromList ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                      New from {listMonthLabel(options.new_from_list)} List
                    </button>
                  </div>
                </div>
              ) : null}

              {priorityGroups.length ? (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">Priority group</div>
                  <div className="flex gap-1 flex-wrap">
                    {priorityGroups.map((g) => (
                      <button key={g.id} type="button" onClick={() => togglePriorityGroup(g)}
                        className={`text-xs px-2 py-1 rounded border ${draft.filters.priority_groups.includes(g.id) ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                        {/* S90 P12A item 3 — the chip says WHICH states it selects.
                            Pressing a Priority Group also toggles its member states
                            (togglePriorityGroup), so a label that hides the members
                            asks the operator to press a control and then read a
                            different control to find out what it did. */}
                        {g.label}
                        {g.states?.length ? <span className="opacity-70"> ({g.states.join(', ')})</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {[['division', options?.divisions, DIV_DISPLAY], ['region', options?.regions, null]].map(([key, values, display]) => (
                values?.length ? (
                  <div key={key}>
                    <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">{key}</div>
                    <div className="flex gap-1 flex-wrap">
                      {values.map((v) => (
                        <button key={v} type="button" onClick={() => toggleIn(key, v)}
                          className={`text-xs px-2 py-1 rounded border ${draft.filters[key].includes(v) ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                          {display?.[v] || v}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}

              {options?.states?.length ? (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">state</div>
                  <div className="flex gap-1 flex-wrap">
                    {options.states.map((v) => (
                      // S90 P12A item 3 — min-w + centred text so two-letter and
                      // longer codes sit on the same grid. Without it the row reads
                      // ragged and the pills look like different controls.
                      <button key={v} type="button" onClick={() => toggleState(v)}
                        className={`text-xs px-2 py-1 rounded border min-w-[2.75rem] text-center ${draft.filters.state.includes(v) ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-sm ${estimateError ? 'text-amber-800' : 'text-[#5A5A5A]'}`}>
                  {estimateError || (draftCount === null ? '' : `≈ ${draftCount} contacts match`)}
                </span>
                <button type="button" onClick={addSegment}
                  disabled={segments.length >= MAX_SEGMENTS || !draft.name || draftCount === null}
                  className="text-sm font-semibold px-3 py-1.5 rounded bg-[#0E2A20] text-white disabled:opacity-50">
                  Add Segment
                </button>
                {segments.length >= MAX_SEGMENTS ? <span className="text-xs text-amber-800">Maximum is {MAX_SEGMENTS} segments.</span> : null}
                <button type="button" onClick={addCatchAll} disabled={segments.some((s) => s.catchAll || s.filters?.newFromList)}
                  className="text-sm px-3 py-1.5 rounded border-2 border-[#0E2A20] text-[#0E2A20] disabled:opacity-40">
                  Add All Remaining Contacts
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {/* ── 3 SCHEDULE ────────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border-2 border-[#0E2A20] p-6 space-y-4">
          <h2 className="font-display font-black text-[#0E2A20] text-xl">3. Schedule</h2>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">Start sending</div>
            <div className="flex gap-1 flex-wrap">
              {START_MODES
                // The two auth options are hidden once the mailbox is connected —
                // there is nothing left to wait for.
                .filter((m) => !(m.authMode && enablesLaunch(connectState)))
                .map((m) => (
                  <button key={m.id} type="button" onClick={() => { setStartMode(m.id); touch(); }}
                    className={`text-xs px-3 py-1 rounded border ${startMode === m.id ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                    {m.label}
                  </button>
                ))}
            </div>
            {startMode === 'custom' ? (
              <input type="datetime-local" value={scheduledAt} onChange={(e) => { setScheduledAt(e.target.value); touch(); }}
                className="mt-2 border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
            ) : null}
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-1">Send window</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SEND_WINDOWS.map((w) => (
                <button key={w.key} type="button" onClick={() => { setWindowKey(w.key); touch(); }}
                  className={`text-xs px-2 py-2 rounded border ${windowKey === w.key ? 'bg-[#0E2A20] text-white border-[#0E2A20]' : 'border-[#0E2A20] text-[#0E2A20]'}`}>
                  {w.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#5A5A5A] mt-1">Send Window (based on coach&apos;s campus timezone)</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[#5A5A5A]">Emails/Day (1–450)</span>
              <input type="number" min={1} max={450} value={dailyCap} onChange={(e) => { setDailyCap(e.target.value); touch(); }}
                className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-[#5A5A5A]">Delay between sends (13–3600 s)</span>
              <input type="number" min={13} max={3600} value={sendDelaySeconds} onChange={(e) => { setSendDelaySeconds(e.target.value); touch(); }}
                className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
            </label>
            <label className="block">
              {/* T-7: SECONDS, not RM's minutes. Porting RM's label unchanged
                  sends 3 -> a 3-SECOND gap. */}
              <span className="text-xs text-[#5A5A5A]">Same-school gap (180–600 s)</span>
              <input type="number" min={180} max={600} value={schoolGapSeconds} onChange={(e) => { setSchoolGapSeconds(e.target.value); touch(); }}
                className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
            </label>
          </div>

          {/* S90 P12A item 5 — this read a bare `~0h`, which named neither what
              was being estimated nor that it was an estimate. It now says both,
              and when the same-school gap is what binds the runtime it says so —
              because "why is this taking six hours" has two different answers and
              the operator can act on one of them. Deliberately NOT a completion
              timestamp: the scheduler defers on recipient timezone and daily
              windows, and promising a clock time we cannot honour is worse than
              a range. */}
          <div className="text-sm text-[#5A5A5A] space-y-0.5">
            {segments.length === 0
              ? <p>Add segments above to see campaign duration estimate.</p>
              : (estimate
                ? (
                  <>
                    <p className="text-[#0E2A20]">
                      {estimate.days <= 1
                        ? `Estimated time to run campaign: ~${estimate.hours}h`
                        : `Estimated time to run campaign: ~${estimate.days} send days`}
                    </p>
                    {estimate.days > 1 ? (
                      <p>{`${estimate.hours_per_day}h sending window per day · up to ${estimate.emails_per_day}/day`}</p>
                    ) : null}
                    {estimate.limited_by === 'same_school_gap' ? (
                      <p className="text-amber-800">
                        {`Paced by the ${estimate.school_gap_seconds}s same-school gap — `}
                        {`${estimate.max_school_size} of these coaches are at one school, and we space those out.`}
                      </p>
                    ) : null}
                    <p className="text-xs">Estimate, not a delivery time — sends also wait for each coach&apos;s local send window.</p>
                  </>
                )
                : <p>—</p>)}
          </div>
        </section>

        {/* ── 4 TEMPLATE ────────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border-2 border-[#0E2A20] p-6 space-y-4">
          <h2 className="font-display font-black text-[#0E2A20] text-xl">4. Template</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {LINK_SLOTS.map(({ token, label }) => {
              const urlState = linkUrlState(links[token]?.url);
              return (
                <div key={token}>
                  <div className="mb-1">
                    <span className="text-xs font-semibold text-[#0E2A20]">{label}</span>
                    {' '}
                    <span className="text-[10px] text-[#5A5A5A]">{`{{${token}}}`}</span>
                  </div>
                  <input value={links[token]?.url || ''} placeholder="URL" disabled={!isDraft}
                    onChange={(e) => { setLinks((l) => ({ ...l, [token]: { ...l[token], url: e.target.value } })); touch(); }}
                    className={`w-full border rounded px-2 py-1 text-sm mb-1 disabled:bg-[#F4F4F2] ${urlState.message ? 'border-red-400' : 'border-[#0E2A20]'}`} />
                  <input value={links[token]?.text || ''} placeholder="Link text" disabled={!isDraft}
                    onChange={(e) => { setLinks((l) => ({ ...l, [token]: { ...l[token], text: e.target.value } })); touch(); }}
                    className="w-full border-2 border-[#0E2A20] rounded px-2 py-1 text-sm disabled:bg-[#F4F4F2]" />
                  {urlState.message ? <p className="text-[10px] text-red-700 mt-0.5">{urlState.message}</p> : null}
                  {/* S90 P12A item 6 — insertion is gated on THIS link validating,
                      so a token can never be written into a body over a URL that
                      will not resolve. Blank and invalid are both disabled; only
                      the invalid one explains itself, because a blank optional
                      field is not an error the operator needs told about. */}
                  <button type="button" disabled={!isDraft || !urlState.ok}
                    onClick={() => insertToken(`{{${token}}}`)}
                    className="mt-1 text-[10px] px-2 py-0.5 rounded border-2 border-[#0E2A20] text-[#0E2A20] disabled:opacity-40">
                    Insert into body
                  </button>
                </div>
              );
            })}
          </div>

          <div>
            <span className="text-xs text-[#5A5A5A]">Subject</span>
            <input ref={subjectRef} value={subject} onChange={(e) => { setSubject(e.target.value); touch(); }}
              className="mt-1 w-full border-2 border-[#0E2A20] rounded px-3 py-2 text-sm" />
            <div className="flex gap-1 flex-wrap mt-1">
              {SUBJECT_TOKENS.map((t) => (
                <button key={t} type="button" onClick={() => insertAt(subjectRef, setSubject, t)}
                  className="text-xs px-2 py-0.5 rounded border-2 border-[#0E2A20] text-[#0E2A20]">{t}</button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-xs text-[#5A5A5A]">Body</span>
            {/* S90 P12A item 7 — the raw HTML textarea is GONE. Jake: "we sure as
                shit weren't writing in HTML." The schema in editor-config.js is
                what keeps the output deterministic for the send-time merge. */}
            <BodyEditor ref={bodyEditorRef} value={body} disabled={!isDraft}
              onChange={(html) => { setBody(html); touch(); }} />
            <div className="flex gap-1 flex-wrap mt-1">
              {MERGE_TOKENS.map((t) => (
                <button key={t} type="button" disabled={!isDraft}
                  onClick={() => insertToken(t)}
                  className="text-xs px-2 py-0.5 rounded border-2 border-[#0E2A20] text-[#0E2A20] disabled:opacity-40">{t}</button>
              ))}
            </div>
            <p className="text-xs text-[#5A5A5A] mt-2">All emails include an unsubscribe link at the bottom.</p>
            {/* S90 P12A item 8 — PLAIN LANGUAGE, AND IT SAYS WHAT VARIANTS ARE FOR
                BEFORE IT SAYS WHEN THEY STOP.
                The old copy was five implementation disqualifiers in a row
                ("no middle blocks to replace", "the link block appears too
                early — a mid-body anchor") with no statement of intent, and it
                rendered identically whether rotation was on or off. 7e0365d
                already made the LIVE reason appear under the carousel when it is
                actually off; this is the standing explanation beside it, written
                for a football office rather than for whoever wrote
                analyzeComposability. The SERVER still decides — the client
                computes nothing. */}
            <details className="mt-2">
              <summary className="text-xs text-[#5A5A5A] cursor-pointer">What variants are for, and when they turn off</summary>
              <div className="text-xs text-[#5A5A5A] mt-1 space-y-1">
                <p>
                  Variants exist so that <strong>two coaches at the same school don&apos;t get
                  word-for-word identical emails.</strong> Each coach at a school gets the next
                  variant in the rotation. Coaches at different schools can get the same one —
                  they&apos;ll never see each other&apos;s mail.
                </p>
                <p>
                  Variants you generate now are <strong>complete emails</strong> — greeting through
                  closing — so all we hold back is your sign-off, which stays exactly as you wrote
                  it on every one. Rotation only needs your email to have a sign-off line at the
                  end.
                </p>
                <p className="text-[11px]">
                  Campaigns whose variants were generated before this change work the older way:
                  only the middle is swapped, so those need at least three paragraphs with a link
                  or sign-off near the end. Regenerate to move a campaign onto the new format.
                </p>
                <p>
                  When it&apos;s off, everyone gets your original email exactly as written. Nothing
                  breaks and nothing is lost.
                </p>
                {/* S90 item 8 — the five conditions in plain terms, for the operator who would
                    rather fix the layout than regenerate. No colour word, no variant_index, no
                    pool, no composable, no "block". */}
                <p className="font-semibold mt-1">Why older versions sometimes don&apos;t fit</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  <li>Your email is shorter than three paragraphs.</li>
                  <li>Your link is in the middle of the email instead of near the end.</li>
                  <li>There&apos;s no closing paragraph after your link.</li>
                  <li>Everything is in one run instead of separate paragraphs.</li>
                  <li>There&apos;s no middle section to swap out.</li>
                </ul>
              </div>
            </details>
          </div>

          <div className="border-t border-[#0E2A20]/15 pt-4">
            {/* 🔴 P-16: the disclosure renders ABOVE the carousel and BEFORE a
                variant can be selected. */}
            <p className="text-xs text-amber-800 mb-2">{AI_DISCLOSURE}</p>
            <button type="button" onClick={generateVariants} disabled={generating || !body || !isDraft}
              className="text-sm font-semibold px-3 py-1.5 rounded border-2 border-[#0E2A20] text-[#0E2A20] disabled:opacity-40">
              {generating ? 'Generating…' : 'Generate 15 versions'}
            </button>
            {variants.length ? (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setVariantIdx((i) => Math.max(0, i - 1))} className="text-xs px-2 py-1 border-2 border-[#0E2A20] rounded">←</button>
                  <span className="text-sm text-[#0E2A20]">
                    {/* S90 — ONE DENOMINATOR, NOT TWO. This read "Original of 16"
                        beside "Variant 15 of 15": both true, counting different
                        things (the pool vs the AI variants), disagreeing in the
                        same widget. A number with its frame stripped is what made
                        the original off-by-one invisible in the first place. The
                        pool size lives in `variantPool` on the campaign read,
                        where it is labelled; it does not belong in a control the
                        operator reads as a count of variants. */}
                    {poolIdx === 0 ? 'Original' : `Variant ${poolIdx} of ${effectivePool - 1}`}
                  </span>
                  <button type="button" onClick={() => setVariantIdx((i) => Math.min(effectivePool - 1, i + 1))} className="text-xs px-2 py-1 border-2 border-[#0E2A20] rounded">→</button>
                  {poolIdx > 0
                    ? <button type="button" onClick={() => regenerateOne(poolIdx - 1)} className="text-xs px-2 py-1 border-2 border-[#0E2A20] rounded">Regenerate</button>
                    : null}
                </div>
                <div className="mt-2 border-2 border-[#0E2A20] rounded p-3 text-sm">
                  {poolIdx === 0
                    /* eslint-disable-next-line react/no-danger */
                    ? <div dangerouslySetInnerHTML={{ __html: body }} />
                    : (variants[poolIdx - 1] === null || variants[poolIdx - 1] === undefined
                      ? <span className="text-amber-800">Generation failed — Regenerate</span>
                      /* eslint-disable-next-line react/no-danger */
                      : <div dangerouslySetInnerHTML={{ __html: variants[poolIdx - 1] }} />)}
                </div>
                {rotationDisabledReason ? (
                  /* S90 — REWRITTEN. The previous copy read "Rotation is OFF — every recipient gets
                     the original body. link block too early (mid-body anchor). These 15 generated
                     variants are stored but will not be sent, by test or by real run." Jake read it
                     live and could not parse it: four internal terms in three sentences, with the
                     server's reason arriving as a lowercase fragment jammed mid-paragraph.
                     Item 8's acceptance line is plain football-staff language, and that failed it.
                     Leads with the consequence in the operator's terms, then the one action. */
                  <div className="text-xs text-amber-800 mt-1 space-y-1">
                    <p className="font-semibold">Every coach on this list will get the exact same email.</p>
                    <p>
                      {`You have ${variants.length} versions saved, but they don't fit this email's layout, so none of them will be used.`}
                    </p>
                    {/* 🔴 GENERATE-ALL IS THE FIX, AND IT IS VERIFIED RATHER THAN ASSUMED.
                        Output of generateVariants() is a whole email carrying aiVariantsFormat
                        'full', which bypasses analyzeComposability entirely — so it clears ALL
                        FIVE conditions at once, not just the one being reported. Measured against
                        every condition: legacy pool 1 -> full pool 3 on "fewer than 3 blocks", "no
                        link/closing block", "content outside <p>" and "no middle blocks". The old
                        advice (move your link) is now the long way round.
                        ⚠ S94 X-1 — THIS USED TO SAY "a regenerated variant", WHICH READ AS THOUGH
                        THE PER-SLOT Regenerate BUTTON WERE EQUALLY SAFE. It is not, and the two are
                        different functions: generateVariants() stamps the array, regenerateOne()
                        cannot (see its comment). That wording is how an operator picks the button
                        that mints the defect this banner is warning about. */}
                    <p>Press <strong>Generate 15 versions</strong> again — new versions work with any layout.</p>
                  </div>
                ) : (
                  <p className="text-xs text-[#5A5A5A] mt-1">
                    Each coach at the same school gets a different version of your email.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </section>

        {/* ── 5 PREVIEW & LAUNCH ────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border-2 border-[#0E2A20] p-6 space-y-4">
          <h2 className="font-display font-black text-[#0E2A20] text-xl">5. Preview &amp; Launch</h2>

          {capacityLine ? <p className="text-sm text-amber-800">{capacityLine}</p> : null}

          {previewError ? <p className="text-sm text-amber-800">{previewError}</p> : null}

          {preview ? (
            <div className="text-sm text-[#0E2A20] space-y-0.5">
              {(preview.counts || []).map((c, i) => (
                <div key={segments[i]?.name || i}>
                  Seg {i + 1}: {segments[i]?.catchAll ? CATCH_ALL_NAME : segments[i]?.name} — {c.net} {segments[i]?.catchAll ? 'remaining' : 'matches'}
                </div>
              ))}
              <div className="font-semibold">Total: {totalNet}</div>
              <div className="text-[#5A5A5A]">Daily cap {dailyCap} · {window8.label}</div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 flex-wrap">
            {/* Relocated from the masthead — see the note there. Placed FIRST
                because on a brand-new campaign it is the only pressable thing
                in this row: Send Test, Queue and Launch all require an id. */}
            <button type="button" onClick={save} disabled={saving || !name || !recruit}
              className={`${BTN_DARK} disabled:opacity-40 disabled:shadow-none`}>
              {saving ? 'Saving…' : 'Save'}
            </button>

            {/* 🔴 NO RECIPIENT INPUT (ruling 35). The address is bound to the
                operator's own mailbox and shown in the LABEL so it is visible
                without being changeable. */}
            <button type="button" onClick={sendTest}
              disabled={!campaignId || !enablesLaunch(connectState) || !me?.team?.email}
              className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}>
              Send Test to {me?.team?.email || '…'}
            </button>

            {startsNow && isAdmin ? (
              <button type="button" onClick={queueAndLaunch} disabled={!launchEnabled}
                className={`${BTN_DARK} disabled:opacity-40 disabled:shadow-none`}>
                {launching ? 'Launching…' : 'Queue & Launch'}
              </button>
            ) : (
              <button type="button" onClick={queueOnly} disabled={!campaignId || dirty}
                className={`${BTN_DARK} disabled:opacity-40 disabled:shadow-none`}>
                Queue
              </button>
            )}
          </div>

          {/* Say WHY, rather than leaving a disabled button unexplained. */}
          {!launchEnabled && startsNow && isAdmin ? (
            <ul className="text-xs text-[#5A5A5A] list-disc pl-5">
              {!campaignId ? <li>Save the campaign first.</li> : null}
              {dirty ? <li>You have unsaved changes.</li> : null}
              {!enablesLaunch(connectState) ? <li>Gmail is not verified for this recruit.</li> : null}
              {!capacityKnown ? <li>Capacity is unknown.</li> : null}
            </ul>
          ) : null}
          {!isAdmin ? (
            <p className="text-xs text-[#5A5A5A]">Launching is admin/manager only. Queue the campaign and an admin can launch it.</p>
          ) : null}
        </section>
      </main>

      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}
